import type { TFunction } from 'i18next';
import type { SurveyOutcome, TeamSurveySummary } from '../../api/workEnvironmentTypes';
import { fullDate } from '../HRDashboard/hmsFormat';
import { nextDeadlineText } from '../HRDashboard/HRWorkEnvironment/Overview/hrOverviewView';
import {
  currentStepIndex,
  PROCESS_STEPS,
  responsePercent,
  stepState,
  type StepState,
} from '../WorkEnvironmentSurveyDetail/surveyProcessView';
import { inspectionReportPath, reportPath } from '../WorkEnvironmentSurveyDetail/surveyRoutes';

const K = 'workEnvironmentDashboard';

export const OUTCOME_COLORS: Record<SurveyOutcome, 'neutral' | 'info' | 'success' | 'warning'> = {
  draft: 'neutral',
  ongoing: 'info',
  archived: 'success',
  stopped: 'warning',
};

/** A survey stops in the risk step when too few answer */
const STOPPED_AT = PROCESS_STEPS.indexOf('risk');

export type OverviewEntry =
  | { kind: 'survey'; survey: TeamSurveySummary }
  | { kind: 'measurement'; measuredAt: string | null; from: number; to: number };

/**
 * The surveys newest first. An evaluation that started a new round links the two rounds, so the
 * measurement stands between the round it starts and the round it evaluated.
 */
export const overviewEntries = (surveys: TeamSurveySummary[]): OverviewEntry[] =>
  surveys.flatMap((survey): OverviewEntry[] =>
    survey.evaluatesRound !== null && survey.round !== null
      ? [
          { kind: 'survey', survey },
          { kind: 'measurement', measuredAt: survey.measuredAt, from: survey.evaluatesRound, to: survey.round },
        ]
      : [{ kind: 'survey', survey }],
  );

/** One primary button per view: the newest survey still on its way */
export const primarySurveyId = (surveys: TeamSurveySummary[]) =>
  surveys.find((survey) => survey.outcome === 'ongoing')?.id ?? null;

export type Segment = StepState | 'stopped';

/** The nine steps as a bar: done, where the survey is, or where it stopped */
export const segments = (survey: TeamSurveySummary): Segment[] =>
  PROCESS_STEPS.map((_step, index) => {
    if (survey.outcome === 'archived') return 'done';
    if (survey.outcome === 'stopped')
      return index < STOPPED_AT ? 'done' : index === STOPPED_AT ? 'stopped' : 'notStarted';
    return stepState(index, currentStepIndex(survey.status));
  });

/** «Steg 3 av 9» and the name of the step, «Fullført» or where it stopped */
export const stepText = (survey: TeamSurveySummary, t: TFunction) => {
  if (survey.outcome === 'archived') return { label: t(`${K}.step.done`), name: t(`${K}.step.archived`) };
  if (survey.outcome === 'stopped')
    return { label: t(`${K}.step.stopped`, { number: STOPPED_AT + 1 }), name: t(`${K}.step.tooFew`) };
  const index = currentStepIndex(survey.status);
  return {
    label: t(`${K}.step.current`, { number: index + 1, total: PROCESS_STEPS.length }),
    name:
      survey.status === 'INSUFFICIENT_RESPONSES'
        ? t(`${K}.step.tooFew`)
        : t(`workEnvironmentProcess.steps.${PROCESS_STEPS[index]}.title`),
  };
};

/** When the survey started, and where it ends: the evaluation ahead, or when it was evaluated or stopped */
export const periodText = (survey: TeamSurveySummary, t: TFunction, language: string) => {
  const start = fullDate(survey.createdAt, language);
  if (survey.outcome === 'draft') return t(`${K}.period.created`, { date: start });
  const end =
    survey.outcome === 'archived' && survey.evaluatedAt
      ? { key: 'evaluated', date: survey.evaluatedAt }
      : survey.outcome === 'stopped' && survey.measuredAt
        ? { key: 'stopped', date: survey.measuredAt }
        : survey.outcome === 'ongoing' && survey.evaluationDate
          ? { key: 'evaluation', date: survey.evaluationDate }
          : null;
  return end
    ? t(`${K}.period.${end.key}`, { start, date: fullDate(end.date, language) })
    : t(`${K}.period.started`, { date: start });
};

export type Fact = { label: string; value: string; highRisk?: boolean };

const responsesFact = (survey: TeamSurveySummary, t: TFunction): Fact => ({
  label: t(`${K}.facts.responses`),
  value: survey.expected
    ? t(`${K}.facts.responsesValue`, {
        responses: survey.responses,
        expected: survey.expected,
        percent: responsePercent(survey.responses, survey.expected),
      })
    : String(survey.responses),
});

/** The numbers that tell how the survey went, and what is due next while it is ongoing */
export const factsOf = (survey: TeamSurveySummary, t: TFunction, language: string): Fact[] => {
  if (survey.outcome === 'draft')
    return survey.expected
      ? [{ label: t(`${K}.facts.recipients`), value: t(`${K}.facts.recipientsValue`, { count: survey.expected }) }]
      : [];
  if (survey.outcome === 'stopped')
    return [
      responsesFact(survey, t),
      ...(survey.minResponses
        ? [{ label: t(`${K}.facts.limit`), value: t(`${K}.facts.limitValue`, { count: survey.minResponses }) }]
        : []),
      { label: t(`${K}.facts.risk`), value: t(`${K}.facts.notCalculated`) },
    ];
  return [
    responsesFact(survey, t),
    ...(survey.risk
      ? [
          {
            label: t(`${K}.facts.highRisk`),
            value: t(`${K}.facts.highRiskValue`, { high: survey.risk.high, total: survey.risk.total }),
            highRisk: survey.risk.high > 0,
          },
        ]
      : []),
    ...(survey.measures
      ? [{ label: t(`${K}.facts.measures`), value: t(`${K}.facts.measuresValue`, survey.measures) }]
      : []),
    ...(survey.next ? [{ label: t(`${K}.facts.next`), value: nextDeadlineText(survey.next, t, language) }] : []),
  ];
};

/** The reports that exist for the survey so far */
export const reportLinks = (survey: TeamSurveySummary, t: TFunction) => [
  ...(survey.reports.report ? [{ label: t(`${K}.links.report`), to: reportPath('team', survey.id) }] : []),
  ...(survey.reports.inspection
    ? [{ label: t(`${K}.links.inspection`), to: inspectionReportPath('team', survey.id) }]
    : []),
];

export type SurveyAction = { label: string; variant: 'primary' | 'secondary' };

/** A draft has nothing to open yet. The newest ongoing survey is continued, the rest are opened. */
export const actionOf = (survey: TeamSurveySummary, primaryId: number | null, t: TFunction): SurveyAction | null => {
  if (survey.outcome === 'draft') return null;
  if (survey.outcome !== 'ongoing') return { label: t(`${K}.actions.open`), variant: 'secondary' };
  return { label: t(`${K}.actions.continue`), variant: survey.id === primaryId ? 'primary' : 'secondary' };
};

export const measurementText = (
  entry: Extract<OverviewEntry, { kind: 'measurement' }>,
  t: TFunction,
  language: string,
) => ({
  label: entry.measuredAt
    ? t(`${K}.measurement.label`, { date: fullDate(entry.measuredAt, language) })
    : t(`${K}.measurement.labelWithoutDate`),
  text: t(`${K}.measurement.text`, { from: entry.from, to: entry.to }),
});

/** The newest survey with areas the inspection report still waits for */
export const surveyWithAreasLeft = (surveys: TeamSurveySummary[]) =>
  surveys.find((survey) => (survey.areasLeft ?? []).length > 0) ?? null;
